import React from 'react'
import ReactMarkdown from 'react-markdown'
import Link from 'next/link'

import { Article, ContentBlock } from '@/slices/blog/mocks/articles'
import { Box, styled } from '@mui/material'

import { pxToRem, ThemeType } from '@/shared/styles/themes'
import { generateHeadingId } from '@/slices/blog/utils/generateHeadingId'
import { ArticleCallout } from './ArticleCallout'
import { ArticleCarousel } from './ArticleCarousel'
import { ArticleCTA } from './ArticleCTA'
import { ArticleNumberedSteps } from './ArticleNumberedSteps'
import { ArticlePullQuote } from './ArticlePullQuote'
import { CustomImage } from '@/components/CustomImage/CustomImage'

export interface ArticleRendererProps {
  article: Article
}

export const ArticleRenderer: React.FC<ArticleRendererProps> = ({ article }) => {
  const { blocks } = article

  if (!blocks || blocks.length === 0) {
    return null
  }

  const markdownComponents = {
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <ContentH1 id={generateHeadingId(String(children))} {...props}>{children}</ContentH1>
    ),
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <ContentH2 id={generateHeadingId(String(children))} {...props}>{children}</ContentH2>
    ),
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <ContentH3 id={generateHeadingId(String(children))} {...props}>{children}</ContentH3>
    ),
    p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => <ContentParagraph {...props} />,
    hr: ({ ...props }: React.HTMLAttributes<HTMLHRElement>) => <ContentDivider {...props} />,
    a: ({ href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
      if (href?.startsWith('/')) {
        return <InternalLink href={href} {...props} />
      }

      return <ReferenceLink {...props} href={href} target="_blank" rel="noopener noreferrer" />
    },
    ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => <ContentList {...props} />,
    ol: ({ ...props }: React.HTMLAttributes<HTMLOListElement>) => <ContentOrderedList {...props} />,
    li: ({ ...props }: React.LiHTMLAttributes<HTMLLIElement>) => <ContentListItem {...props} />,
  }

  const renderBlock = (block: ContentBlock, index: number): React.ReactNode => {
    switch (block.type) {
      case 'markdown':
        return (
          <ReactMarkdown key={index} components={markdownComponents}>
            {block.content}
          </ReactMarkdown>
        )

      case 'cta':
        return <ArticleCTA key={index} block={block} />

      case 'carousel':
        return (
          <ArticleCarousel
            key={index}
            carousel={{
              id: `carousel-${index}`,
              type: 'carousel',
              images: block.images,
              autoplay: block.autoplay,
              loop: block.loop,
            }}
          />
        )

      case 'callout':
        return <ArticleCallout key={index} block={block} />

      case 'image':
        return (
          <ImageBlock key={index}>
            <CustomImage
              src={block.src}
              alt={block.alt}
              variant="horizontal"
              objectFit="cover"
              borderRadius={12}
            />
            {block.caption && <ImageCaption>{block.caption}</ImageCaption>}
          </ImageBlock>
        )

      case 'faq':
        return (
          <FAQSection key={index}>
            <ContentH2>Frequently Asked Questions</ContentH2>
            {block.questions.map((q, i) => (
              <FAQItem key={i}>
                <FAQQuestion>{q.question}</FAQQuestion>
                <FAQAnswer>{q.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQSection>
        )

      case 'key-takeaway':
        return (
          <KeyTakeawayWrapper key={index}>
            <KeyTakeawayLabel>Key takeaways</KeyTakeawayLabel>
            {block.title && (
              <KeyTakeawayTitle>{block.title}</KeyTakeawayTitle>
            )}
            <KeyTakeawayList>
              {block.points.map((point, i) => (
                <KeyTakeawayItem key={i}>
                  <KeyTakeawayBullet />
                  <KeyTakeawayText>{point}</KeyTakeawayText>
                </KeyTakeawayItem>
              ))}
            </KeyTakeawayList>
          </KeyTakeawayWrapper>
        )

      case 'stat-highlight':
        return (
          <StatHighlightWrapper key={index}>
            <StatValue>{block.stat}</StatValue>
            <StatDescription>{block.description}</StatDescription>
            {block.source && (
              <StatSource>{block.source}</StatSource>
            )}
          </StatHighlightWrapper>
        )

      case 'pull-quote':
        return <ArticlePullQuote key={index} block={block} />

      case 'numbered-steps':
        return <ArticleNumberedSteps key={index} block={block} />

      default:
        return null
    }
  }

  return <ArticleContent>{blocks.map(renderBlock)}</ArticleContent>
}

const ArticleContent = styled(Box)(({ theme }) => ({
  fontSize: pxToRem(17),
  lineHeight: 1.7,
  color: '#2C2A28',

  '& strong': {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },

  '& em': {
    fontStyle: 'italic',
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(19),
    lineHeight: 1.75,
  },
}))

const ImageBlock = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(32)} 0`,
  borderRadius: pxToRem(12),
  overflow: 'hidden',

  [theme.breakpoints.down('md')]: {
    margin: `${pxToRem(24)} 0`,
  },
}))

const ImageCaption = styled('figcaption')(({ theme }) => ({
  fontSize: pxToRem(14),
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  textAlign: 'center',
  marginTop: pxToRem(12),

  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(12),
  },
}))

const ContentH1 = styled('h1')(({ theme }) => ({
  fontSize: pxToRem(32),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginTop: pxToRem(44),
  marginBottom: pxToRem(24),
  lineHeight: 1.2,
  letterSpacing: '-0.01em',

  '&:first-of-type': {
    marginTop: 0,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(42),
    marginTop: pxToRem(56),
    marginBottom: pxToRem(28),
  },
}))

const ContentH2 = styled('h2')(({ theme }) => ({
  fontSize: pxToRem(26),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginTop: pxToRem(48),
  marginBottom: pxToRem(20),
  lineHeight: 1.25,
  letterSpacing: '-0.005em',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(32),
    marginTop: pxToRem(56),
    marginBottom: pxToRem(24),
  },
}))

const ContentH3 = styled('h3')(({ theme }) => ({
  fontSize: pxToRem(22),
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginTop: pxToRem(32),
  marginBottom: pxToRem(16),
  lineHeight: 1.35,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(26),
    marginTop: pxToRem(40),
    marginBottom: pxToRem(18),
  },
}))

const ContentParagraph = styled('p')(({ theme }) => ({
  fontSize: pxToRem(17),
  color: '#2C2A28',
  lineHeight: 1.7,
  marginBottom: pxToRem(20),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(19),
    lineHeight: 1.75,
    marginBottom: pxToRem(24),
  },
}))

const ContentDivider = styled('hr')(({ theme }) => ({
  width: '100%',
  height: 0,
  marginTop: pxToRem(40),
  marginBottom: pxToRem(40),
  border: 'none',
  textAlign: 'center' as const,
  overflow: 'visible',

  '&::before': {
    content: '"\\2022  \\2022  \\2022"',
    fontSize: pxToRem(16),
    color: '#4A7FA5',
    letterSpacing: pxToRem(4),
  },

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(48),
    marginBottom: pxToRem(48),
  },
}))

const linkStyles = (theme: ThemeType) => ({
  color: theme.palette.custom.terracotta,
  textDecoration: 'underline',
  wordBreak: 'break-word' as const,
  transition: 'all 0.2s ease',
  cursor: 'pointer',

  '&:hover': {
    color: theme.palette.custom.deepTerracotta,
  },

  '&:visited': {
    color: theme.palette.custom.terracotta,
  },
})

const ReferenceLink = styled('a')(({ theme }) => linkStyles(theme))

const InternalLink = styled(Link)(({ theme }) => linkStyles(theme))

const ContentList = styled('ul')(({ theme }) => ({
  marginBottom: pxToRem(20),
  paddingLeft: pxToRem(24),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(24),
  },
}))

const ContentOrderedList = styled('ol')(({ theme }) => ({
  marginBottom: pxToRem(20),
  paddingLeft: pxToRem(24),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(24),
  },
}))

const ContentListItem = styled('li')({
  marginBottom: pxToRem(8),
})

const FAQSection = styled(Box)(({ theme }) => ({
  marginTop: pxToRem(48),
  marginBottom: pxToRem(32),

  [theme.breakpoints.up('md')]: {
    marginTop: pxToRem(56),
    marginBottom: pxToRem(40),
  },
}))

const FAQItem = styled(Box)(({ theme }) => ({
  paddingTop: pxToRem(24),
  paddingBottom: pxToRem(24),
  borderTop: `1px solid ${theme.palette.divider}`,

  '&:last-of-type': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(28),
    paddingBottom: pxToRem(28),
  },
}))

const FAQQuestion = styled('h3')(({ theme }) => ({
  fontSize: pxToRem(18),
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginTop: 0,
  marginBottom: pxToRem(10),
  lineHeight: 1.4,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(20),
  },
}))

const FAQAnswer = styled('p')(({ theme }) => ({
  fontSize: pxToRem(16),
  color: theme.palette.primary.main,
  lineHeight: 1.8,
  margin: 0,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
  },
}))

// Key Takeaway block styles
const KeyTakeawayWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(36)} 0`,
  paddingTop: pxToRem(20),
  borderTop: `1px solid ${theme.palette.divider}`,

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(44)} 0`,
    paddingTop: pxToRem(24),
  },
}))

const KeyTakeawayLabel = styled('span')(({ theme }) => ({
  display: 'block',
  fontSize: pxToRem(12),
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: theme.palette.custom.terracotta,
  marginBottom: pxToRem(10),
}))

const KeyTakeawayTitle = styled('h3')(({ theme }) => ({
  fontSize: pxToRem(20),
  fontWeight: 700,
  color: theme.palette.primary.main,
  margin: 0,
  marginBottom: pxToRem(16),
  lineHeight: 1.3,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(22),
  },
}))

const KeyTakeawayList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(12),
})

const KeyTakeawayItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: pxToRem(12),
})

const KeyTakeawayBullet = styled('span')(({ theme }) => ({
  width: pxToRem(6),
  height: pxToRem(6),
  borderRadius: '50%',
  backgroundColor: theme.palette.custom.terracotta,
  flexShrink: 0,
  marginTop: pxToRem(9),
}))

const KeyTakeawayText = styled('p')(({ theme }) => ({
  fontSize: pxToRem(15),
  color: theme.palette.primary.main,
  lineHeight: 1.7,
  margin: 0,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))

// Stat Highlight block styles
const StatHighlightWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(36)} 0`,
  padding: `${pxToRem(32)} ${pxToRem(24)}`,
  textAlign: 'center',
  backgroundColor: theme.palette.primary.main,
  borderRadius: pxToRem(12),

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(44)} 0`,
    padding: `${pxToRem(40)} ${pxToRem(32)}`,
  },
}))

const StatValue = styled('p')(({ theme }) => ({
  fontSize: pxToRem(36),
  fontWeight: 700,
  color: '#4A7FA5',
  lineHeight: 1.2,
  margin: 0,
  marginBottom: pxToRem(12),
  letterSpacing: '-0.02em',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
    marginBottom: pxToRem(16),
  },
}))

const StatDescription = styled('p')(({ theme }) => ({
  fontSize: pxToRem(16),
  fontWeight: 400,
  color: theme.palette.primary.contrastText,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: pxToRem(480),
  marginLeft: 'auto',
  marginRight: 'auto',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
  },
}))

const StatSource = styled('p')(({ theme }) => ({
  fontSize: pxToRem(12),
  fontWeight: 400,
  color: theme.palette.primary.contrastText,
  opacity: 0.6,
  lineHeight: 1.4,
  margin: 0,
  marginTop: pxToRem(12),
  fontStyle: 'italic',

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(13),
    marginTop: pxToRem(16),
  },
}))
